import type { ReactNode } from "react";

import { cn } from "./lib/cn";

export interface SplitAuthLayoutFeature {
  /** Kararlı kimlik — React anahtarı. */
  key: string;
  icon: ReactNode;
  title: string;
  description: string;
}

export interface SplitAuthLayoutProps {
  /** Sol sütunun büyük başlığı ("Sözleşme yönetimini daha kolay hale getirin."). */
  heroTitle: ReactNode;
  heroDescription: string;
  /** Sol sütunun üç özellik satırı. Metin domain bilgisi — kit bunu İÇERMEZ, çağıran verir. */
  features: readonly SplitAuthLayoutFeature[];
  /** Sağ kartın başlığı — ekranın `<h1>`'i. */
  title: string;
  description?: string;
  /** Kartın üstündeki ikon çipi (mockup: kilit/zarf ikonu, yalnız bazı ekranlarda). */
  icon?: ReactNode;
  /**
   * Geniş ekrandaki tanıtım görseli. Kaynak/alt metin çağıranın alan bilgisidir;
   * kit yalnız görselin yerleşimini sahiplenir.
   */
  heroImageSrc?: string;
  heroImageAlt?: string;
  children: ReactNode;
  /** Sayfa üstündeki logo bloğunun sağı — mockup dil seçici/tema anahtarı, yalnız ÜRÜNDE
   * varsa çağıran doldurur; kit hiçbir kontrolü varsayılan olarak dayatmaz. */
  headerEnd?: ReactNode;
  /** Sayfa altı — telif + yasal bağlantılar, yalnız o rotalar üründe varsa. */
  footer?: ReactNode;
  className?: string;
}

/**
 * Oturum ekranlarının iki sütunlu kabuğu — UI Tur 2 §5/§7.15 (`AuthLayout`'un yerini alır).
 *
 * ⚠ Mockup'taki masa fotoğrafı ATLANDI (§7.15): yerine sol alt köşede yumuşak bir radyal
 * gradyan var — kit'in kendi asset'i olmayan bir stüdyo fotoğrafını taşıması hem gerçekçi
 * değil hem de her tema/ekran boyutunda güncellenmesi gereken bir bakım yüküydü.
 *
 * ⚠ Sol sütun DAR EKRANDA GİZLENİR ("mobilde işlevi kes" değil — tanıtım metni, kontrol
 * değil; kabuğun taşıdığı hiçbir düğme/bağlantı orada yok, hepsi formun içinde).
 */
export function SplitAuthLayout({
  heroTitle,
  heroDescription,
  features,
  title,
  description,
  icon,
  heroImageSrc,
  heroImageAlt = "",
  children,
  headerEnd,
  footer,
  className,
}: SplitAuthLayoutProps) {
  return (
    <div className={cn("min-h-screen bg-canvas px-5 py-5 sm:px-8 sm:py-8", className)}>
      <div className="mx-auto flex min-h-[calc(100vh-2.5rem)] w-full max-w-[1320px] flex-col sm:min-h-[calc(100vh-4rem)]">
        <header className="flex items-center justify-between gap-3 py-2 sm:py-3">
          <div className="flex items-center gap-2.5">
            <span
              aria-hidden="true"
              className="flex size-9 items-center justify-center rounded-[11px] bg-primary text-base font-bold text-on-inverse shadow-sm"
            >
              C
            </span>
            <span className="text-[19px] font-extrabold tracking-[0.13em] text-fg-heading uppercase">
              Closer
            </span>
          </div>
          {headerEnd}
        </header>

        <main className="grid flex-1 items-stretch gap-8 py-5 lg:grid-cols-[minmax(0,1.08fr)_minmax(420px,0.92fr)] lg:gap-14 lg:py-10">
          {/* Tanıtım alanı yalnız geniş ekranda. Formun işlevi her genişlikte bağımsızdır. */}
          <section className="hidden min-h-[650px] flex-col justify-between overflow-hidden rounded-[28px] border border-border-default bg-surface lg:flex">
            <div className="max-w-[560px] px-10 pt-11 xl:px-14 xl:pt-14">
              <p className="mb-5 text-xs font-bold tracking-[0.16em] text-accent uppercase">
                Closer çalışma alanı
              </p>
              <p className="max-w-[560px] text-[42px] leading-[1.08] font-bold tracking-[-0.035em] text-fg-strong">
                {heroTitle}
              </p>
              <p className="mt-5 max-w-[470px] text-[16px] leading-7 text-fg-muted">
                {heroDescription}
              </p>
            </div>

            {heroImageSrc ? (
              <div className="relative mx-7 mt-8 overflow-hidden rounded-[20px] border border-border-subtle bg-muted xl:mx-10">
                <img
                  src={heroImageSrc}
                  alt={heroImageAlt}
                  className="aspect-[1.56/1] w-full object-cover object-left-bottom"
                />
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,transparent_48%,color-mix(in_srgb,var(--color-primary)_18%,transparent))]"
                />
              </div>
            ) : null}

            <ul className="grid grid-cols-3 border-t border-border-subtle px-7 py-7 xl:px-10">
              {features.map((feature, index) => (
                <li
                  key={feature.key}
                  className={cn(
                    "min-w-0 px-4 first:pl-0",
                    index < features.length - 1 && "border-r border-border-subtle",
                  )}
                >
                  <span
                    aria-hidden="true"
                    className="mb-3 flex size-9 items-center justify-center rounded-[10px] bg-primary-soft text-primary [&_svg]:size-[18px]"
                  >
                    {feature.icon}
                  </span>
                  <span className="block text-[13px] leading-5 font-bold text-fg-heading">
                    {feature.title}
                  </span>
                  <span className="mt-1.5 block text-xs leading-5 text-fg-muted">
                    {feature.description}
                  </span>
                </li>
              ))}
            </ul>
          </section>

          <section className="flex w-full items-center justify-center py-6 lg:min-h-[650px] lg:rounded-[28px] lg:border lg:border-border-default lg:bg-surface lg:px-10 lg:py-10">
            <div className="w-full max-w-[460px] rounded-[24px] border border-border-default bg-surface p-7 shadow-md sm:p-10 lg:rounded-none lg:border-0 lg:bg-transparent lg:p-0 lg:shadow-none">
              <div className="mb-8 flex flex-col gap-3">
                {icon ? (
                  <span
                    aria-hidden="true"
                    className="flex size-11 items-center justify-center rounded-[12px] bg-primary-soft text-primary [&_svg]:size-5"
                  >
                    {icon}
                  </span>
                ) : null}
                <h1 className="text-[30px] leading-[1.18] font-bold tracking-[-0.025em] text-fg-strong">
                  {title}
                </h1>
                {description ? (
                  <p className="max-w-[360px] text-sm leading-6 text-fg-muted">{description}</p>
                ) : null}
              </div>
              {children}
            </div>
          </section>
        </main>

        {footer ? (
          <footer className="pb-2 text-center text-[13px] text-fg-faint">{footer}</footer>
        ) : null}
      </div>
    </div>
  );
}
